import { AnswerReviewCurrentRevision, AnswerReviewFieldViewModel, AnswerReviewFilter, AnswerReviewSectionViewModel, AnswerReviewViewModel } from './AnswerReviewWorkbench.types';
import { getEditor } from './formSubmissionFields';
import { answerTargetKey, isDraftItemKey, RecursiveAnswerNode, simpleValueError } from './recursiveAnswer';
export function recursiveReviewField(node: RecursiveAnswerNode): AnswerReviewFieldViewModel {
  const schema = node.schema;
  const value = node.kind === 'simple' ? node.value : null;
  const assessment = node.assessment;
  let currentRevision: AnswerReviewCurrentRevision | null = node.isNull && !assessment ? null : {
    origin: 'USER',
    confidence: null,
    value
  };
  if (assessment?.status === 'NOT_FOUND' || assessment?.status === 'EMPTY') {
    currentRevision = {
      origin: 'AI',
      confidence: assessment.status,
      value: null
    };
  } else if (assessment && value !== null) {
    currentRevision = {
      origin: 'AI',
      confidence: assessment.status,
      value
    };
  }
  let editor: AnswerReviewFieldViewModel['editor'] = {
    kind: 'conclusion'
  };
  if (schema.fieldType === 'number') {
    editor = {
      kind: 'decimal'
    };
  } else if (schema.fieldType !== 'object' && schema.fieldType !== 'array') {
    editor = getEditor(schema);
  }
  return {
    id: answerTargetKey(node.target),
    label: schema.label,
    isActive: true,
    editor,
    currentRevision,
    evidence: node.references,
    reasoning: assessment?.reasoning,
    userRevisions: [],
    validationMessage: node.kind === 'simple' ? simpleValueError(schema, node.value) : undefined
  };
}

/** Null containers and empty arrays are conclusions; populated containers group leaves. */
export function recursiveReviewFields(node: RecursiveAnswerNode): AnswerReviewFieldViewModel[] {
  if (node.kind === 'simple' || node.isNull || node.kind === 'array' && node.items.length === 0) {
    return [recursiveReviewField(node)];
  }
  if (node.kind === 'object') {
    return node.properties.flatMap(recursiveReviewFields);
  }
  return node.items.flatMap(item => recursiveReviewFields(item.node));
}
export function recursiveNodeMatchesFilter(node: RecursiveAnswerNode, filter: AnswerReviewFilter, retainedFieldIds: (string | undefined)[] = []): boolean {
  // A newly inserted item must stay editable even under an AI-only filter.
  if (isDraftItemKey(node.target.arrayItemID)) {
    return true;
  }
  if (node.kind === 'object' && !node.isNull) {
    return node.properties.some(child => recursiveNodeMatchesFilter(child, filter, retainedFieldIds));
  }
  if (node.kind === 'array' && node.items.length > 0) {
    return node.items.some(item => recursiveNodeMatchesFilter(item.node, filter, retainedFieldIds));
  }
  const field = recursiveReviewField(node);
  return retainedFieldIds.includes(field.id) || fieldMatchesFilter(field, filter);
}
export interface AnswerReviewSummary {
  total: number;
  answered: number;
  high: number;
  medium: number;
  notFound: number;
  edited: number;
  evidence: number;
  sectionsWithEvidence: number;
}
export function getActiveFields(fields: AnswerReviewFieldViewModel[]): AnswerReviewFieldViewModel[] {
  return fields.flatMap(field => {
    const children = getActiveFields(field.children ?? []);
    if (!field.isActive) {
      return children;
    }
    const ownFields = field.recursiveNode ? recursiveReviewFields(field.recursiveNode) : [field];
    return [...ownFields, ...children];
  });
}
export function getAnswerReviewSummary(review: AnswerReviewViewModel): AnswerReviewSummary {
  const activeFieldsBySection = review.sections.map(section => getActiveFields(section.fields));
  const fields = activeFieldsBySection.flat();
  return {
    total: fields.length,
    answered: fields.filter(field => field.currentRevision !== null && field.currentRevision.confidence !== 'NOT_FOUND').length,
    high: fields.filter(field => field.currentRevision?.confidence === 'HIGH').length,
    medium: fields.filter(field => field.currentRevision?.confidence === 'MEDIUM').length,
    notFound: fields.filter(field => field.currentRevision?.confidence === 'NOT_FOUND').length,
    edited: fields.filter(field => field.currentRevision?.origin === 'USER').length,
    evidence: fields.reduce((total, field) => total + field.evidence.length, 0),
    sectionsWithEvidence: activeFieldsBySection.filter(sectionFields => sectionFields.some(field => field.evidence.length > 0)).length
  };
}
export function fieldMatchesFilter(field: AnswerReviewFieldViewModel, filter: AnswerReviewFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  const confidence = field.currentRevision?.confidence;
  if (filter === 'needsReview') {
    return field.currentRevision?.origin !== 'USER' && confidence !== 'HIGH';
  }
  if (filter === 'high') {
    return confidence === 'HIGH';
  }
  return field.currentRevision?.origin === 'USER';
}
export function filterAnswerReviewSections(sections: AnswerReviewSectionViewModel[], filter: AnswerReviewFilter, retainedFieldIds: (string | undefined)[] = []): AnswerReviewSectionViewModel[] {
  const matches = (field: AnswerReviewFieldViewModel) => retainedFieldIds.includes(field.id) || fieldMatchesFilter(field, filter);
  const filterFields = (fields: AnswerReviewFieldViewModel[]): AnswerReviewFieldViewModel[] => fields.flatMap(field => {
    const children = filterFields(field.children ?? []);
    // Grouping does not make an independent child conditional on its parent.
    if (!field.isActive) {
      return children;
    }
    const ownMatch = field.recursiveNode ? recursiveNodeMatchesFilter(field.recursiveNode, filter, retainedFieldIds) : matches(field);
    if (!ownMatch && children.length === 0) {
      return [];
    }
    return [{
      ...field,
      children
    }];
  });
  return sections.flatMap(section => {
    const fields = filterFields(section.fields);
    return fields.length > 0 ? [{
      ...section,
      fields
    }] : [];
  });
}